import { DeviceDto } from '@/core/models/types'

export interface PortfolioFilters {
    measureType: string | null
    atex: string | null
    protocol: string | null
    ipRating: string | null
    vendor: string | null
}

export const EMPTY_PORTFOLIO_FILTERS: PortfolioFilters = {
    measureType: null,
    atex: null,
    protocol: null,
    ipRating: null,
    vendor: null,
}

const norm = (value: string | null | undefined) => (value ?? '').trim().toLowerCase()

const matchesSearch = (device: DeviceDto, search: string): boolean => {
    if (!search) return true
    const needle = norm(search)
    return (
        norm(device.vendor).includes(needle) ||
        norm(device.displayName).includes(needle) ||
        norm(device.title).includes(needle) ||
        norm(device.deviceMeasurementType).includes(needle)
    )
}

const matchesFilters = (device: DeviceDto, filters: PortfolioFilters): boolean => {
    if (filters.measureType && norm(device.deviceMeasurementType) !== norm(filters.measureType)) return false
    if (filters.atex && norm(device.atex) !== norm(filters.atex)) return false
    if (filters.protocol && norm(device.protocol) !== norm(filters.protocol)) return false
    if (filters.ipRating && norm(device.ipRating) !== norm(filters.ipRating)) return false
    if (filters.vendor && norm(device.vendor) !== norm(filters.vendor)) return false
    return true
}

export const filterPortfolioDevices = (devices: DeviceDto[], filters: PortfolioFilters, search: string): DeviceDto[] =>
    devices.filter((d) => matchesSearch(d, search) && matchesFilters(d, filters))

export const sortPortfolioDevices = (devices: DeviceDto[]): DeviceDto[] =>
    [...devices].sort((a, b) => {
        const vendorCmp = (a.vendor ?? '').localeCompare(b.vendor ?? '')
        if (vendorCmp !== 0) return vendorCmp
        return (a.title ?? '').localeCompare(b.title ?? '')
    })

const uniqueSorted = (values: (string | null | undefined)[]): string[] =>
    Array.from(new Set(values.map((v) => v?.trim()).filter((v): v is string => !!v))).sort((a, b) => a.localeCompare(b))

export const derivePortfolioFilterOptions = (devices: DeviceDto[]) => ({
    measureType: uniqueSorted(devices.map((d) => d.deviceMeasurementType)),
    atex: uniqueSorted(devices.map((d) => d.atex)),
    protocol: uniqueSorted(devices.map((d) => d.protocol)),
    ipRating: uniqueSorted(devices.map((d) => d.ipRating)),
    vendor: uniqueSorted(devices.map((d) => d.vendor)),
})
